import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000, // 15 second timeout for requests
});

// Cache API cleared on logout (no-op - caching removed for reliability)
export function clearApiCache() {}

// Add auth token interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add retry logic for transient failures (helps with MongoDB cold starts)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (!config) return Promise.reject(error);
    
    if (
      config._retry ||
      !navigator.onLine ||
      axios.isCancel(error) ||
      (error.response && error.response.status >= 400 && error.response.status < 500) ||
      config.url?.includes('/auth/')
    ) {
      return Promise.reject(error);
    }
    
    if (!error.response || error.response.status >= 500) {
      config._retry = true;
      await new Promise(resolve => setTimeout(resolve, 1000));
      return api(config);
    }
    
    return Promise.reject(error);
  }
);

// Auth
export const login = (data) => api.post('/auth/login', data);
export const register = (data) => api.post('/auth/register', data);
export const getMe = () => api.get('/auth/me');
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const resetPassword = (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword });
export const changePassword = (currentPassword, newPassword) => api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword });
export const updateProfile = (data) => api.put('/auth/profile', data);
export const updateHomeLocation = (data) => api.put('/auth/home-location', data);
export const updateDashboardColor = (color) => api.put('/auth/dashboard-color', { color });
export const deleteAccount = () => api.delete('/auth/account');

// Subscriptions
export const getSubscriptionPlans = () => api.get('/subscription/plans');
export const getSubscriptionStatus = () => api.get('/subscription/status');
export const getUsageStats = () => api.get('/subscription/usage');
export const createCheckoutSession = (planId, originUrl) => api.post('/subscription/checkout', { plan_id: planId, origin_url: originUrl });
export const getCheckoutStatus = (sessionId) => api.get(`/subscription/checkout/status/${sessionId}`);
export const cancelSubscription = () => api.post('/subscription/cancel');
export const getAIUsage = () => api.get('/subscription/ai-usage');
export const purchaseAICredits = (quantity = 1) => api.post('/subscription/purchase-ai-credits', { quantity });

// Referrals
export const getReferralInfo = () => api.get('/referral/info');
export const getReferralStats = () => api.get('/referral/stats');
export const applyReferralCode = (code) => api.post('/referral/apply', { referral_code: code });

// Email Reminders
export const toggleTripReminders = (tripId, enabled) => api.patch(`/trips/${tripId}/reminders?enabled=${enabled}`);
export const checkAndSendReminders = () => api.post('/reminders/check');

// Trips
export const getTrips = () => api.get('/trips');
export const getTrip = (tripId) => api.get(`/trips/${tripId}`);
export const getTripGpxData = (tripId) => api.get(`/trips/${tripId}/gpx-data`);
export const createTrip = (data) => api.post('/trips', data);
export const createTripFromAIPlan = (data) => api.post('/trips/from-ai-plan', data, { timeout: 120000 }); // 120 second timeout for geocoding
export const updateTrip = (tripId, data) => api.put(`/trips/${tripId}`, data);
export const deleteTrip = (tripId) => api.delete(`/trips/${tripId}`);
export const toggleArchiveTrip = (tripId) => api.patch(`/trips/${tripId}/archive`);
export const leaveTrip = (tripId) => api.post(`/trips/${tripId}/leave`);
export const duplicateTrip = (tripId, data) => api.post(`/trips/${tripId}/duplicate`, data);
export const updateTripColor = (tripId, backgroundColor) => 
  api.patch(`/trips/${tripId}/color`, { background_color: backgroundColor });

// GPX Route Data - Multiple Files Support
export const saveTripGpx = (tripId, gpxData) => api.put(`/trips/${tripId}/gpx`, { gpx_data: gpxData });
export const addGpxFile = (tripId, gpxFile) => api.post(`/trips/${tripId}/gpx/files`, { gpx_file: gpxFile });
export const deleteGpxFile = (tripId, gpxId) => api.delete(`/trips/${tripId}/gpx/files/${gpxId}`);
export const renameGpxFile = (tripId, gpxId, name) => api.put(`/trips/${tripId}/gpx/files/${gpxId}/rename`, { name });
export const deleteTripGpx = (tripId) => api.delete(`/trips/${tripId}/gpx`);

// Waypoints
export const getWaypointCategories = () => api.get('/waypoint-categories');
export const getTripWaypoints = (tripId) => api.get(`/trips/${tripId}/waypoints`);
export const getMyWaypoints = () => api.get('/my-waypoints');
export const createWaypoint = (data) => api.post('/waypoints', data);
export const updateWaypoint = (waypointId, data) => api.put(`/waypoints/${waypointId}`, data);
export const deleteWaypoint = (waypointId) => api.delete(`/waypoints/${waypointId}`);

// Trip Members
export const getTripMembers = (tripId) => api.get(`/trips/${tripId}/members`);
export const createTripMember = (tripId, data) => api.post(`/trips/${tripId}/members`, data);
export const updateTripMember = (tripId, memberId, data) => api.put(`/trips/${tripId}/members/${memberId}`, data);
export const deleteTripMember = (tripId, memberId) => api.delete(`/trips/${tripId}/members/${memberId}`);
export const linkMemberToUser = (tripId, memberId, email) => api.put(`/trips/${tripId}/members/${memberId}/link?email=${encodeURIComponent(email)}`);
export const unlinkMemberFromUser = (tripId, memberId) => api.put(`/trips/${tripId}/members/${memberId}/unlink`);

// Collaborator Location Sharing
export const updateMyLocation = (tripId, latitude, longitude, accuracy, checkedInAt = null) => 
  api.post(`/trips/${tripId}/location`, { latitude, longitude, accuracy, checked_in_at: checkedInAt });
export const stopSharingLocation = (tripId) => api.delete(`/trips/${tripId}/location`);
export const getCollaboratorLocations = (tripId) => api.get(`/trips/${tripId}/locations`);

// In-App Notifications
export const getNotifications = (limit = 50) => api.get(`/notifications?limit=${limit}`);
export const getUnreadNotificationCount = () => api.get('/notifications/unread-count');
export const markNotificationRead = (notificationId) => api.patch(`/notifications/${notificationId}/read`);
export const markAllNotificationsRead = () => api.post('/notifications/mark-all-read');
export const deleteNotification = (notificationId) => api.delete(`/notifications/${notificationId}`);

// Trip Sharing
export const shareTrip = (tripId, data) => api.post(`/trips/${tripId}/share`, data);
export const removeCollaborator = (tripId, email) => api.delete(`/trips/${tripId}/share/${email}`);
export const inviteCollaborator = (tripId, email, sendEmail = true, role = 'contributor') => 
  api.post(`/trips/${tripId}/invite`, { email, send_email: sendEmail, role });

// Collaboration Management
export const getCollaborationRoles = () => api.get('/collaboration-roles');
export const getTripCollaborators = (tripId) => api.get(`/trips/${tripId}/collaborators`);
export const updateCollaboratorRole = (tripId, email, role) => 
  api.put(`/trips/${tripId}/collaborators/${encodeURIComponent(email)}/role`, { role });
export const deleteCollaborator = (tripId, email) => 
  api.delete(`/trips/${tripId}/collaborators/${encodeURIComponent(email)}`);
export const getMyRole = (tripId) => api.get(`/trips/${tripId}/my-role`);

export const getTripBudget = (tripId) => api.get(`/trips/${tripId}/budget`);
export const getDetailedBudget = (tripId) => api.get(`/trips/${tripId}/budget/detailed`);
export const getPerMemberBudget = (tripId) => api.get(`/trips/${tripId}/budget/per-member`);

// Travel Analytics (Premium)
export const getTravelAnalytics = (year = null) => {
  const params = year ? `?year=${year}` : '';
  return api.get(`/analytics${params}`);
};

// Google Places Search
export const searchPlaces = (query, location = null) => {
  const params = new URLSearchParams({ query });
  if (location) params.append('location', location);
  return api.get(`/places/search?${params.toString()}`);
};

// Notes
export const getTripNotes = (tripId) => api.get(`/trips/${tripId}/notes`);
export const createNote = (data) => api.post('/notes', data);
export const updateNote = (noteId, data) => api.put(`/notes/${noteId}`, data);
export const deleteNote = (noteId) => api.delete(`/notes/${noteId}`);

// Activities
export const getTripActivities = (tripId) => api.get(`/trips/${tripId}/activities`);
export const createActivity = (data) => api.post('/activities', data);
export const updateActivity = (activityId, data) => api.put(`/activities/${activityId}`, data);
export const deleteActivity = (activityId) => api.delete(`/activities/${activityId}`);
export const uploadActivityTicket = (activityId, file, label = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('label', label);
  return api.post(`/activities/${activityId}/ticket`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteActivityTicket = (activityId, ticketIndex) => api.delete(`/activities/${activityId}/ticket/${ticketIndex}`);
export const updateActivityTicketLabel = (activityId, ticketIndex, label) => api.put(`/activities/${activityId}/ticket/${ticketIndex}/label`, { label });

// Hotels
export const getTripHotels = (tripId) => api.get(`/trips/${tripId}/hotels`);
export const createHotel = (data) => api.post('/hotels', data);
export const updateHotel = (hotelId, data) => api.put(`/hotels/${hotelId}`, data);
export const deleteHotel = (hotelId) => api.delete(`/hotels/${hotelId}`);

// Pictures
export const getTripPictures = (tripId) => api.get(`/trips/${tripId}/pictures`);
export const createPicture = (data) => api.post('/pictures', data);
export const updatePicture = (pictureId, data) => api.put(`/pictures/${pictureId}`, data);
export const deletePicture = (pictureId) => api.delete(`/pictures/${pictureId}`);

// Documents
export const getTripDocuments = (tripId) => api.get(`/trips/${tripId}/documents`);
export const createDocument = (data) => api.post('/documents', data);
export const updateDocument = (documentId, data) => api.put(`/documents/${documentId}`, data);
export const deleteDocument = (documentId) => api.delete(`/documents/${documentId}`);

// Restaurants
export const getTripRestaurants = (tripId) => api.get(`/trips/${tripId}/restaurants`);
export const createRestaurant = (data) => api.post('/restaurants', data);
export const updateRestaurant = (restaurantId, data) => api.put(`/restaurants/${restaurantId}`, data);
export const deleteRestaurant = (restaurantId) => api.delete(`/restaurants/${restaurantId}`);

// Calendar aggregate
export const getTripCalendar = (tripId) => api.get(`/trips/${tripId}/calendar`);

// File Upload
export const uploadImage = (file, tripId) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('trip_id', tripId);
  return api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Cloudinary Upload (for images - stores in cloud, not MongoDB)
export const getCloudinarySignature = (folder = 'trips', resourceType = 'image') => 
  api.get(`/cloudinary/signature?folder=${folder}&resource_type=${resourceType}`);

export const uploadToCloudinary = async (file, folder = 'trips') => {
  try {
    // Get signature from backend
    console.log('[Cloudinary] Getting signature...');
    const sigResponse = await getCloudinarySignature(folder);
    const sig = sigResponse.data;
    console.log('[Cloudinary] Got signature for cloud:', sig.cloud_name);
    
    // Upload directly to Cloudinary
    const formData = new FormData();
    formData.append('file', file);
    formData.append('api_key', sig.api_key);
    formData.append('timestamp', sig.timestamp);
    formData.append('signature', sig.signature);
    formData.append('folder', sig.folder);
    
    console.log('[Cloudinary] Uploading file:', file.name, file.size, 'bytes');
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
      { method: 'POST', body: formData }
    );
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Cloudinary] Upload failed:', response.status, errorText);
      throw new Error(`Failed to upload image: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('[Cloudinary] Upload successful:', result.secure_url);
    
    // Return in a format similar to our existing upload
    return {
      data: {
        url: result.secure_url,
        public_id: result.public_id,
        width: result.width,
        height: result.height,
        format: result.format,
        size: result.bytes
      }
    };
  } catch (error) {
    console.error('[Cloudinary] Upload error:', error);
    throw error;
  }
};

// Base64 upload for Safari compatibility
export const uploadToCloudinaryBase64 = async (base64Data, folder = 'trips') => {
  try {
    console.log('[Cloudinary] Getting signature for base64 upload...');
    const sigResponse = await getCloudinarySignature(folder);
    const sig = sigResponse.data;
    console.log('[Cloudinary] Got signature for cloud:', sig.cloud_name);
    
    // Upload directly to Cloudinary using base64
    const formData = new FormData();
    formData.append('file', base64Data); // Cloudinary accepts base64 data URLs
    formData.append('api_key', sig.api_key);
    formData.append('timestamp', sig.timestamp);
    formData.append('signature', sig.signature);
    formData.append('folder', sig.folder);
    
    console.log('[Cloudinary] Uploading base64 data...');
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
      { method: 'POST', body: formData }
    );
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Cloudinary] Base64 upload failed:', response.status, errorText);
      throw new Error(`Failed to upload image: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('[Cloudinary] Base64 upload successful:', result.secure_url);
    
    return {
      data: {
        url: result.secure_url,
        public_id: result.public_id,
        width: result.width,
        height: result.height,
        format: result.format,
        size: result.bytes
      }
    };
  } catch (error) {
    console.error('[Cloudinary] Base64 upload error:', error);
    throw error;
  }
};

export const deleteImage = (imageId) => api.delete(`/images/${imageId}`);

// Geocoding (client-side using Nominatim) with improved error handling
let lastGeocodeTime = 0;
const GEOCODE_DEBOUNCE_MS = 1500; // Minimum time between requests

// Helper to try geocoding with a single query
const tryGeocode = async (query) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`,
      {
        headers: {
          'User-Agent': 'MyTripPlanner/1.0'
        }
      }
    );
    
    if (!response.ok) {
      return null;
    }
    
    const data = await response.json();
    if (data && data.length > 0) {
      return {
        latitude: parseFloat(data[0].lat),
        longitude: parseFloat(data[0].lon),
        display_name: data[0].display_name
      };
    }
    return null;
  } catch (_) {
    // Swallow CORS/network errors so the loop continues to the next variant
    // and ultimately falls through to the backend /api/geocode fallback.
    return null;
  }
};

export const geocodeAddress = async (address, tripDestination = '') => {
  // Debounce check
  const now = Date.now();
  const timeSinceLastRequest = now - lastGeocodeTime;
  if (timeSinceLastRequest < GEOCODE_DEBOUNCE_MS) {
    await new Promise(resolve => setTimeout(resolve, GEOCODE_DEBOUNCE_MS - timeSinceLastRequest));
  }
  lastGeocodeTime = Date.now();
  
  try {
    // Build list of search queries to try (most specific to least)
    const searchQueries = [];
    const destCity = tripDestination ? tripDestination.split(',')[0].trim() : '';
    
    // Strategy 1: Full address as-is
    searchQueries.push(address);
    
    // Strategy 2: Address + destination city (if not already included)
    if (destCity && !address.toLowerCase().includes(destCity.toLowerCase())) {
      searchQueries.push(`${address}, ${destCity}`);
    }
    
    // Strategy 3: Just the place/business name + city
    const placeName = address.split(',')[0].split('(')[0].trim();
    if (placeName !== address && destCity) {
      searchQueries.push(`${placeName}, ${destCity}`);
    }
    
    // Strategy 4: Remove country codes and try again
    const simplifiedAddress = address.replace(/\b[A-Z]{2,3}\s*\d{5}(-\d{4})?\b/g, '').trim();
    if (simplifiedAddress !== address) {
      searchQueries.push(simplifiedAddress);
    }
    
    // Remove duplicates
    const uniqueQueries = [...new Set(searchQueries.map(q => q.toLowerCase()))].map((q, i) => searchQueries.find(sq => sq.toLowerCase() === q));
    
    // Try each query
    for (const query of uniqueQueries) {
      const result = await tryGeocode(query);
      if (result) {
        console.log(`[Geocode] Found "${address}" using query: "${query}"`);
        return { data: result };
      }
      // Small delay between attempts
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    // Final fallback: route through backend /api/geocode which has a Google Places
    // fallback for typo-tolerant geocoding (e.g. "Civitaveccia" → Civitavecchia).
    try {
      const backendRes = await api.get('/geocode', { params: { address } });
      if (backendRes?.data?.latitude != null && backendRes?.data?.longitude != null) {
        console.log(`[Geocode] Backend (Google Places fallback) resolved "${address}"`);
        return { data: backendRes.data };
      }
    } catch (be) {
      // Continue to error throw below
    }
    
    throw new Error('Address not found. Try:\n• Adding the full street address\n• Including city and state/country\n• Using a landmark name instead');
  } catch (error) {
    if (error.message.includes('not found') || error.message.includes('Address not found')) {
      throw error;
    }
    throw new Error('Could not connect to geocoding service. Check your internet connection.');
  }
};

// Packing List - Categories
export const getTripPackingCategories = (tripId) => api.get(`/trips/${tripId}/packing-categories`);
export const createPackingCategory = (data) => api.post('/packing-categories', data);
export const deletePackingCategory = (categoryId) => api.delete(`/packing-categories/${categoryId}`);

// Packing List - Items
export const getTripPackingItems = (tripId) => api.get(`/trips/${tripId}/packing-items`);
export const createPackingItem = (data) => api.post('/packing-items', data);
export const updatePackingItem = (itemId, data) => api.put(`/packing-items/${itemId}`, data);
export const togglePackingItem = (itemId) => api.patch(`/packing-items/${itemId}/toggle`);
export const deletePackingItem = (itemId) => api.delete(`/packing-items/${itemId}`);
export const createBulkPackingItems = (tripId, items) => api.post(`/trips/${tripId}/packing-items/bulk`, items);

// Todos
export const getTripTodos = (tripId) => api.get(`/trips/${tripId}/todos`);
export const createTodo = (data) => api.post('/todos', data);
export const updateTodo = (todoId, data) => api.put(`/todos/${todoId}`, data);
export const toggleTodo = (todoId) => api.patch(`/todos/${todoId}/toggle`);
export const deleteTodo = (todoId) => api.delete(`/todos/${todoId}`);

// Flights
export const getTripFlights = (tripId) => api.get(`/trips/${tripId}/flights`);
export const createFlight = (data) => api.post('/flights', data);
export const updateFlight = (flightId, data) => api.put(`/flights/${flightId}`, data);
export const deleteFlight = (flightId) => api.delete(`/flights/${flightId}`);
export const parseFlightConfirmation = (data) => api.post('/flights/parse-confirmation', data);
export const uploadTransportTicket = (flightId, file, label = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('label', label);
  return api.post(`/flights/${flightId}/ticket`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteTransportTicket = (flightId, ticketIndex) => api.delete(`/flights/${flightId}/ticket/${ticketIndex}`);

// Hotels parse confirmation
export const parseHotelConfirmation = (data) => api.post('/hotels/parse-confirmation', data);

// Car Rentals (uses flights/transportation endpoint with transport_type: rental_car)
export const parseCarRentalConfirmation = (data) => api.post('/rentals/parse-confirmation', data);

// Cruise confirmation parsing
export const parseCruiseConfirmation = (data) => api.post('/parse-cruise-confirmation', data);

// Cruise excursions
export const getCruiseExcursionsCount = (cruiseId) => api.get(`/cruises/${cruiseId}/excursions/count`);
export const getCruiseExcursions = (cruiseId) => api.get(`/cruises/${cruiseId}/excursions`);
export const updateCruiseItinerary = (cruiseId, ports) => api.put(`/cruises/${cruiseId}/itinerary`, { itinerary_ports: ports });

// Expenses (standalone)
export const getTripExpenses = (tripId) => api.get(`/trips/${tripId}/expenses`);
export const createExpense = (data) => api.post('/expenses', data);
export const updateExpense = (expenseId, data) => api.put(`/expenses/${expenseId}`, data);
export const deleteExpense = (expenseId) => api.delete(`/expenses/${expenseId}`);
export const getExpenseCategories = () => api.get('/expense-categories');

// Receipts
export const getTripReceipts = (tripId) => api.get(`/trips/${tripId}/receipts`);
export const uploadReceipt = (tripId, data) => api.post(`/trips/${tripId}/receipts`, data);
export const getReceipt = (receiptId) => api.get(`/receipts/${receiptId}`);
export const deleteReceipt = (receiptId) => api.delete(`/receipts/${receiptId}`);
export const linkReceiptToExpense = (receiptId, expenseId) => api.post(`/receipts/${receiptId}/link/${expenseId}`);
export const getReceiptScanQuota = () => api.get('/receipts/scan-quota');
export const scanReceiptWithAI = (receiptId) => api.post(`/receipts/${receiptId}/scan`);

// Meals
export const getTripMeals = (tripId) => api.get(`/trips/${tripId}/meals`);
export const createMeal = (data) => api.post('/meals', data);
export const updateMeal = (mealId, data) => api.put(`/meals/${mealId}`, data);
export const deleteMeal = (mealId) => api.delete(`/meals/${mealId}`);
export const getMealTypes = () => api.get('/meal-types');

// Journal
export const getTripJournal = (tripId) => api.get(`/trips/${tripId}/journal`);
export const createJournalEntry = (data) => api.post('/journal', data);
export const updateJournalEntry = (entryId, data) => api.put(`/journal/${entryId}`, data);
export const deleteJournalEntry = (entryId) => api.delete(`/journal/${entryId}`);

// Budget (detailed)
export const getDetailedTripBudget = (tripId) => api.get(`/trips/${tripId}/budget/detailed`);

// Templates
export const getTemplates = () => api.get('/templates');
export const createTemplate = (data) => api.post('/templates', data);
export const deleteTemplate = (templateId) => api.delete(`/templates/${templateId}`);
export const applyTemplate = (tripId, templateId) => api.post(`/trips/${tripId}/apply-template?template_id=${templateId}`);
export const saveAsTemplate = (tripId, name, description = '') => api.post(`/trips/${tripId}/save-as-template?name=${encodeURIComponent(name)}&description=${encodeURIComponent(description)}`);

// AI Itinerary Templates
export const saveAIItineraryAsTemplate = (data) => api.post('/templates/ai-itinerary', data);
export const applyAITemplate = (tripId, templateId) => api.post(`/trips/${tripId}/apply-ai-template?template_id=${templateId}`);

// Weather
export const getWeather = (lat, lon, startDate, endDate) => api.get(`/weather?lat=${lat}&lon=${lon}&start_date=${startDate}&end_date=${endDate}`);

// AI Trip Planner
export const generateAITripPlan = (data) => api.post('/ai/generate-trip-plan', data, { timeout: 90000 }); // 90 second timeout for AI generation

// PDF Export
export const exportTripPdf = async (tripId) => {
  const token = localStorage.getItem('token');
  const response = await api.get(`/trips/${tripId}/export/pdf`, {
    responseType: 'blob',
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return response;
};

// Day-by-day Itinerary PDF Export
export const exportItineraryPdf = async (tripId) => {
  const token = localStorage.getItem('token');
  const response = await api.get(`/trips/${tripId}/export/itinerary-pdf`, {
    responseType: 'blob',
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return response;
};

// Admin APIs
export const checkAdminStatus = () => api.get('/admin/check');
export const adminSearchUsers = (email) => api.get(`/admin/users/search?email=${encodeURIComponent(email)}`);
export const adminGetLifetimeMembers = () => api.get('/admin/users/lifetime-members');
export const adminGrantLifetime = (userId) => api.post(`/admin/users/${userId}/grant-lifetime`);
export const adminRevokeLifetime = (userId) => api.post(`/admin/users/${userId}/revoke-lifetime`);
export const adminGetStats = () => api.get('/admin/stats');
export const adminGetInfrastructure = () => api.get('/admin/infrastructure');

// My Map APIs
export const getMyMapData = () => api.get('/my-map/all-waypoints');
export const getMyMapWaypoints = () => api.get('/my-map/waypoints');
export const createMyMapWaypoint = (data) => api.post('/my-map/waypoints', data);
export const updateMyMapWaypoint = (waypointId, data) => api.put(`/my-map/waypoints/${waypointId}`, data);
export const deleteMyMapWaypoint = (waypointId) => api.delete(`/my-map/waypoints/${waypointId}`);
export const checkInMyMapWaypoint = (waypointId) => api.post(`/my-map/waypoints/${waypointId}/check-in`);

// My Map GPX APIs
export const getMyMapGpxFiles = () => api.get('/my-map/gpx-files');
export const addMyMapGpxFile = (gpxFile) => api.post('/my-map/gpx-files', { gpx_file: gpxFile });
export const deleteMyMapGpxFile = (gpxId) => api.delete(`/my-map/gpx-files/${gpxId}`);
export const renameMyMapGpxFile = (gpxId, name) => api.put(`/my-map/gpx-files/${gpxId}/rename`, { name });
export const clearMyMapGpxFiles = () => api.delete('/my-map/gpx-files');

// My Map Collections APIs
export const getMyMapCollections = () => api.get('/my-map/collections');
export const createMyMapCollection = (data) => api.post('/my-map/collections', data);
export const updateMyMapCollection = (collectionId, data) => api.put(`/my-map/collections/${collectionId}`, data);
export const deleteMyMapCollection = (collectionId) => api.delete(`/my-map/collections/${collectionId}`);

// My Map Waypoint Photos APIs
export const uploadWaypointPhoto = (waypointId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/my-map/waypoints/${waypointId}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteWaypointPhoto = (waypointId, photoIndex) => api.delete(`/my-map/waypoints/${waypointId}/photos/${photoIndex}`);

// Friends & Social APIs
export const setUsername = (username) => api.put('/profile/username', { username });
export const searchUsers = (q) => api.get(`/users/search?q=${encodeURIComponent(q)}`);
export const getFriends = () => api.get('/friends');
export const getFriendRequests = () => api.get('/friends/requests');
export const sendFriendRequest = (data) => api.post('/friends/request', data);
export const acceptFriendRequest = (requestId) => api.put(`/friends/requests/${requestId}/accept`);
export const declineFriendRequest = (requestId) => api.put(`/friends/requests/${requestId}/decline`);
export const removeFriend = (friendId) => api.delete(`/friends/${friendId}`);
export const inviteFriendByEmail = (email) => api.post('/friends/invite', { email });

// Friend Groups APIs
export const getFriendGroups = () => api.get('/friends/groups');
export const createFriendGroup = (data) => api.post('/friends/groups', data);
export const updateFriendGroup = (groupId, data) => api.put(`/friends/groups/${groupId}`, data);
export const deleteFriendGroup = (groupId) => api.delete(`/friends/groups/${groupId}`);

// Enhanced Check-In APIs
export const getWaypointCheckIns = (waypointId) => api.get(`/my-map/waypoints/${waypointId}/check-ins`);
export const createWaypointCheckIn = (waypointId, data) => api.post(`/my-map/waypoints/${waypointId}/check-in`, data);
export const uploadCheckInPhoto = (waypointId, checkInId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/my-map/waypoints/${waypointId}/check-in/photo?checkin_id=${checkInId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteWaypointCheckIn = (waypointId, checkInId) => api.delete(`/my-map/waypoints/${waypointId}/check-ins/${checkInId}`);

// My Map User Location Check-In (like trip collaborator check-ins)
export const createMyMapLocationCheckIn = (data) => api.post('/my-map/location-checkin', data);
export const getMyMapLocationCheckIn = () => api.get('/my-map/location-checkin');
export const deleteMyMapLocationCheckIn = (checkinId = null) => 
  checkinId 
    ? api.delete(`/my-map/location-checkin?checkin_id=${checkinId}`)
    : api.delete('/my-map/location-checkin');
export const uploadMyMapLocationPhoto = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/my-map/location-checkin/photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getFriendLocations = () => api.get('/my-map/friend-locations');
export const updateWaypointSharing = (waypointId, data) => api.put(`/my-map/locations/${waypointId}/sharing`, data);
export const updateGpxSharing = (gpxId, data) => api.put(`/my-map/gpx-files/${gpxId}/sharing`, data);
export const getSharedWithMe = () => api.get('/my-map/shared');

// Elevation lookup
export const lookupElevation = (coordinates) => api.post('/elevation/lookup', { coordinates }, { timeout: 60000 });

/**
 * Convert a Google Places photo URL to our proxy URL that fetches fresh photos.
 * Google Places photo references expire, so we proxy through our backend.
 */
export const getProxiedImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  // Match Google Places photo URLs and extract the place_id
  const placesMatch = imageUrl.match(/places\.googleapis\.com\/v1\/places\/([^/]+)\/photos\//);
  if (placesMatch) {
    const placeId = placesMatch[1];
    return `${API}/places/${placeId}/photo`;
  }
  // Rewrite old deployment host URLs for uploaded images to current host
  const imagesMatch = imageUrl.match(/\/api\/images\/([a-f0-9-]+)/);
  if (imagesMatch && !imageUrl.startsWith(BACKEND_URL)) {
    return `${API}/images/${imagesMatch[1]}`;
  }
  return imageUrl;
};

export default api;
